import {
  Row,
  Col,
  Typography,
  Table,
  Button,
  message,
  Descriptions,
} from "antd";
import { routes } from "common/routes";
import { generateHeaders, getSlotName } from "common/utils";
import { AppPageHeader } from "components/AppPageHeader";
import { useAppDispatch } from "hooks/useAppDispatch";
import { useAppSelector } from "hooks/useAppSelector";
import { useDocumentTitle } from "hooks/usePageTitle";
import { useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { cancelMiddleMileMapping, removeProcessingMiddleMileMapping, saveMiddleMileMapping } from "store/middle-mile/middleMileSlice";

const MESSAGE_KEY = "SAVE_MIDDLE_MILE_MAPPING";

export const SaveMiddleMileMappjngPage = () => {
  useDocumentTitle("Middle Mile: Save Mapping");
  const { processingMiddleMileMapping, status, error } = useAppSelector(
    (state) => state.middleMile
  );
  const { slots } = useAppSelector((state) => state.requisition);
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  useEffect(() => {
    const { timeUploaded } = processingMiddleMileMapping;
    if (timeUploaded) {
      const uploadedTime = new Date(timeUploaded).getTime();
      const currentTime = new Date().getTime();
      const minsPassed = (currentTime - uploadedTime) / 1000 / 60;

      if (minsPassed >= 15) {
        dispatch(removeProcessingMiddleMileMapping());
      }
    }

    if (!Object.keys(processingMiddleMileMapping).length) {
      navigate(`/${routes.MIDDLE_MILE.MIDDLE_MILE_UPLOAD}`);
    }
  }, [dispatch, processingMiddleMileMapping, navigate]);

  useEffect(() => {
    if (status === "loading") {
      message.loading({ content: "saving middle mile mapping", key: MESSAGE_KEY });
    } else if (status === "success") {
      message.success({
        content: "Middle Mile mapping saved!",
        duration: 5,
        key: MESSAGE_KEY,
      });
    } else if (status === "cancelling") {
      message.loading({ content: "Cancelling Middle Mile mapping", key: MESSAGE_KEY });
    } else if (status === "error") {
      message.error({ content: error, key: MESSAGE_KEY });
    }

    return () => {
      message.destroy();
    };
  }, [status, error]);

  let content;
  const { headerMapping } = processingMiddleMileMapping;
  const { Text } = Typography;

  const generateMemoizedHeaders = useMemo(
    () => generateHeaders(headerMapping),
    [headerMapping]
  );

  const onSaveUserMapping = () => {
    dispatch(saveMiddleMileMapping());
  };

  const onCancelSave = () => {
    dispatch(cancelMiddleMileMapping());
  };

  if (!Object.keys(processingMiddleMileMapping).length) {
    content = <Text>Nothing to show here!</Text>;
  } else {
    const headers = generateMemoizedHeaders || {};
    const successRows = processingMiddleMileMapping.successRows;
    const data = successRows?.map((row, i) => ({ ...row, key: `data-${i}` }));
    const columns = [
      ...Object.keys(headers).map(
        (key) =>
          ({
            key,
            title: headers[key],
            dataIndex: key,
          } as any)
      ),
    ];
    content = (
      <Table
        size="small"
        columns={columns}
        dataSource={data}
        pagination={false}
        scroll={{ y: 500 }}
        summary={() => (
          <Table.Summary fixed="bottom">
            <Table.Summary.Row>
              <Table.Summary.Cell index={0} colSpan={2}>
                <Row>
                  <Col>
                    <Button
                      type="primary"
                      onClick={onSaveUserMapping}
                      loading={status === "loading"}
                      disabled={status === "loading"}
                    >
                      Save Mapping
                    </Button>
                  </Col>
                  <Col style={{ justifyContent: "left", marginLeft: "20px" }}>
                    <Button
                      onClick={onCancelSave}
                      disabled={status === "loading"}
                    >
                      Cancel
                    </Button>
                  </Col>
                </Row>
              </Table.Summary.Cell>
            </Table.Summary.Row>
          </Table.Summary>
        )}
      ></Table>
    );
  }

  return (
    <>
      <Row>
        <Col>
          <AppPageHeader title="Save Middle Mile Mapping" />
        </Col>
      </Row>
      <Row style={{ marginBottom: "1rem" }}>
        {!!(slots && processingMiddleMileMapping.slot) && (
          <Descriptions
            size="small"
            layout="vertical"
            labelStyle={{ fontWeight: "bold" }}
            bordered
            column={{ xs: 2 }}
          >
            <Descriptions.Item label="Date">
              {processingMiddleMileMapping.successRows?.at(0)?.deliveryDate}
            </Descriptions.Item>
            <Descriptions.Item label="Slot">
              {getSlotName(slots, processingMiddleMileMapping.slot)}
            </Descriptions.Item>
          </Descriptions>
        )}
      </Row>
      <Row>
        <Col xs={24}>{content}</Col>
      </Row>
    </>
  );
};
